import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import { confirm, IconCellRenderer } from '@trueprodigy/components';
import Tooltip from '@material-ui/core/Tooltip';
import styled from 'styled-components';
import Fab from '@material-ui/core/Fab';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import AgGrid from '@components/AgGrid/AgGrid';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

import { hasRight } from '@utils';
import { RIGHTS } from '@constants';
import { userDataSelector } from '@selectors/global';
import { ownerEventsSelector } from '@modules/appraisal/selectors/owner';
import { propertySelector } from '@modules/appraisal/selectors/property';
import * as ownerActions from '@modules/appraisal/actions/owner';
import {
  viewOnlySelector,
} from '@modules/appraisal/selectors/global';
import EventDetail from './EventDetail';

const buildColumnDefs = ({ hasDeleteRight, onDelete, onEdit, disabled }) => {
  let iconsParam = [];
  if (!disabled) {
    iconsParam = [
      {
        icon: 'fa-edit',
        onClick: (_, record) => onEdit(record),
      },
    ];
    if (hasDeleteRight()) {
      iconsParam = iconsParam.concat([
        {
          icon: 'fa-trash',
          onClick: (_, record) => onDelete(record),
        },
      ]);
    }
  }

  return [
    {
      headerName: 'Actions',
      field: 'eventID',
      width: 100,
      maxWidth: 100,
      minWidth: 100,
      cellRenderer: IconCellRenderer,
      cellRendererParams: { icons: iconsParam },
    },
    { headerName: 'Event ID', field: 'eventID' },
    { headerName: 'Form', field: 'formName' },
    { headerName: 'Event Type', field: 'eventType' },
    { headerName: 'Updated By', field: 'updatedBy' },
    { headerName: 'Update Dt', field: 'updateDt' },
    { headerName: 'Tags', field: 'tags' },
  ];
};

const OwnerEvents = ({
  data,
  hasDeleteRight,
  onDelete,
  eventID,
  isEventOpen,
  openEventEdit,
  openEventNew,
  closeEvent,
  ownerID,
  property,
  isReadOnly,
  viewOnly
}) => {
  const certified =
    property.getIn(['data', 'rollCorrGroupStatus']) === 'Certified';
  return (
    <div>
      {!isEventOpen ? (
        <OwnerEvents.Container>
          <OwnerEvents.ListingControls>
            <OwnerEvents.Title>OWNER EVENTS</OwnerEvents.Title>
            <Tooltip title="Add Event">
              <Fab
                aria-label="Add Event"
                color="primary"
                disabled={certified || isReadOnly || viewOnly}
                onClick={openEventNew}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </OwnerEvents.ListingControls>
          <div
            className="ag-theme-material"
            style={{ height: 'calc(100vh - 150px)', width: '100%' }}
          >
            <OwnerEvents.AgWrapper>
              <AgGrid
                animateRows
                autoResizeToFit
                columnDefs={buildColumnDefs({
                  hasDeleteRight,
                  onDelete,
                  onEdit: openEventEdit,
                  disabled: certified || isReadOnly,
                })}
                disablePreferenceSave
                defaultColDef={{ resizable: true, sortable: true }}
                name="OwnerEventsTable"
                rowData={
                  data
                    ? data
                        .get('data')
                        .toJS()
                        .map(row =>
                          _.pick(row, [
                            'eventID',
                            'formName',
                            'eventType',
                            'updatedBy',
                            'updateDt',
                            'tags',
                          ]),
                        )
                    : []
                }
                onGridReady={params => params.columnApi.autoSizeColumns()}
              />
            </OwnerEvents.AgWrapper>
          </div>
        </OwnerEvents.Container>
      ) : (
        <React.Fragment>
          <OwnerEvents.Wrapper>
            <Breadcrumbs arial-label="Breadcrumb">
              <Link to="#" onClick={closeEvent}>
                Events
              </Link>
              <Typography color="textPrimary">
                {eventID ? eventID : 'New Event '}
              </Typography>
            </Breadcrumbs>
          </OwnerEvents.Wrapper>
          <EventDetail
            eventID={eventID}
            ownerID={ownerID}
            onClose={closeEvent}
            onCreateSuccess={eventID => openEventEdit({ eventID })}
          />
        </React.Fragment>
      )}
    </div>
  );
};

OwnerEvents.propTypes = {
  data: PropTypes.any,
  eventID: PropTypes.string,
  property: PropTypes.any,
  fetchOwnerEvents: PropTypes.func.isRequired,
  formValues: PropTypes.object.isRequired,
  hasDeleteRight: PropTypes.func.isRequired,
  isEventOpen: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.any,
  ownerID: PropTypes.number,
  onDelete: PropTypes.func.isRequired,
  openEventEdit: PropTypes.func.isRequired,
  openEventNew: PropTypes.func.isRequired,
  closeEvent: PropTypes.func.isRequired,
  viewOnly: PropTypes.any,
};

OwnerEvents.Wrapper = styled.div`
  background-color: transparent;
  padding: 15px;
`;

OwnerEvents.Container = styled.div`
  background-color: white;
  padding: 10px;
`;

OwnerEvents.ListingControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

OwnerEvents.AgWrapper = styled.div`
  height: 100%;
  overflow: auto;
`;

OwnerEvents.Title = styled.h4`
  flex: ${({ flex }) => flex || 1};
  font-family: Roboto, sans-serif;
  visibility: visible;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.87);
`;

const mapStateToProps = createStructuredSelector({
  data: ownerEventsSelector,
  property: propertySelector,
  userData: userDataSelector,
  viewOnly: viewOnlySelector,
  formValues: state => {
    const valuesPath = ['form', 'propertyEventForm', 'values'];
    return state.hasIn(valuesPath) ? state.getIn(valuesPath).toJS() : {};
  },
});

const mapDispatchToProps = {
  deleteOwnerEvent: ownerActions.deleteOwnerEvent,
  fetchOwnerEvents: ownerActions.fetchOwnerEvents,
};

const enhance = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    hasDeleteRight: ({ userData }) => () => {
      return hasRight(
        userData.get('userrights').toJS(),
        RIGHTS.EVENTS.DELETEEVENT,
      );
    },
    onDelete: ({ ownerID, deleteOwnerEvent, fetchOwnerEvents }) => record => {
      return confirm('owner event').then(() =>
        deleteOwnerEvent({
          eventID: record.eventID,
        }).then(() => fetchOwnerEvents({ ownerID })),
      );
    },
  }),
  withStateHandlers(
    ({ isEventOpen = false, eventID = null }) => ({
      isEventOpen,
      eventID,
    }),
    {
      openEventEdit: () => record => ({
        isEventOpen: true,
        eventID: `${record.eventID}`,
      }),
      openEventNew: () => () => ({
        isEventOpen: true,
        eventID: null,
      }),
      closeEvent: (_, { ownerID, fetchOwnerEvents }) => () => {
        fetchOwnerEvents({ ownerID });
        return { eventID: null, isEventOpen: false };
      },
    },
  ),
  lifecycle({
    componentDidMount() {
      const { ownerID, fetchOwnerEvents } = this.props;

      fetchOwnerEvents({ ownerID });
    },
  }),
);

export default enhance(OwnerEvents);
